import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Eye, EyeOff, Pencil, Plus, Trash2, X } from "lucide-react";
import { Switch } from "@/components/ui/switch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AUTH = { withCredentials: true };

export const COLLECTION_SPECS = {
  board: {
    title: "Founding board",
    singular: "board member",
    settingKey: "show_board",
    hint: "Cards appear in the People section of the About page. Empty seats are shown as placeholders until 9 members are added.",
    fields: [
      { key: "name", label: "Name", required: true },
      { key: "role", label: "Role / seat", required: true, placeholder: "e.g. Senior academic leader" },
      { key: "institution", label: "Institution / organisation" },
      { key: "bio", label: "Short bio", type: "textarea" },
      { key: "photo", label: "Photo", type: "image" },
      { key: "order", label: "Order", type: "number" },
    ],
    summary: (it) => [it.name, it.role],
  },
  events: {
    title: "Events & news",
    singular: "event or news item",
    settingKey: "show_events",
    hint: "Upcoming items (by date) appear first on the Home and Programmes pages. Past dates are shown greyed under the upcoming list.",
    fields: [
      { key: "title", label: "Title", required: true },
      { key: "date", label: "Date", type: "date", required: true },
      { key: "time", label: "Time", placeholder: "e.g. 10:00 – 13:00 IST" },
      { key: "location", label: "Location / format", placeholder: "Pune (in person) or Online" },
      { key: "type", label: "Type", type: "select", options: ["Workshop", "Masterclass", "Roundtable", "Expert session", "Podcast", "News"] },
      { key: "description", label: "Description", type: "textarea" },
      { key: "link", label: "Link", placeholder: "/contact or https://..." },
      { key: "order", label: "Order", type: "number" },
    ],
    summary: (it) => [it.title, `${it.date}${it.type ? ` · ${it.type}` : ""}`],
  },
  spotlights: {
    title: "Member spotlights",
    singular: "spotlight",
    settingKey: "show_spotlights",
    hint: "Rotating quotes shown on the Home and Membership pages.",
    fields: [
      { key: "name", label: "Member name", required: true },
      { key: "role", label: "Role", placeholder: "e.g. Associate Professor, Commerce" },
      { key: "institution", label: "Institution / city" },
      { key: "quote", label: "Quote", type: "textarea", required: true },
      { key: "photo", label: "Photo", type: "image" },
      { key: "order", label: "Order", type: "number" },
    ],
    summary: (it) => [it.name, it.quote?.slice(0, 80)],
  },
};

const inputCls = "w-full rounded-md border border-line bg-sand px-3 py-2 text-sm focus:border-pine focus:ring-0";

function FieldInput({ f, value, onChange, testid }) {
  if (f.type === "textarea") return <textarea rows={3} value={value} onChange={(e) => onChange(e.target.value)} className={`${inputCls} resize-y`} placeholder={f.placeholder} data-testid={testid} />;
  if (f.type === "select")
    return (
      <select value={value} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={testid}>
        <option value="">—</option>
        {f.options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    );
  if (f.type === "image") {
    const pick = (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (file.size > 300_000) return toast.error("Please use an image under 300 KB");
      const r = new FileReader();
      r.onload = () => onChange(r.result);
      r.readAsDataURL(file);
    };
    return (
      <div className="space-y-2">
        {value && <img src={value} alt="" className="h-24 w-24 rounded-md border border-line object-cover" data-testid={`${testid}-preview`} />}
        <input value={value?.startsWith("data:") ? "" : value} onChange={(e) => onChange(e.target.value)} placeholder="/images/… or https://…" className={inputCls} data-testid={testid} />
        <input type="file" accept="image/*" onChange={pick} className="text-xs text-ink/60" data-testid={`${testid}-upload`} />
      </div>
    );
  }
  return <input type={f.type || "text"} value={value} onChange={(e) => onChange(e.target.value)} className={inputCls} placeholder={f.placeholder} data-testid={testid} />;
}

function ItemForm({ name, spec, initial, onDone, onCancel }) {
  const blank = Object.fromEntries(spec.fields.map((f) => [f.key, f.type === "number" ? 0 : ""]));
  const [form, setForm] = useState({ ...blank, ...(initial || {}) });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (initial?.id) await axios.put(`${API}/admin/${name}/${initial.id}`, form, AUTH);
      else await axios.post(`${API}/admin/${name}`, form, AUTH);
      toast.success(initial?.id ? "Updated" : "Added");
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="rounded-[10px] border border-pine/40 bg-surface p-6" data-testid={`${name}-form`}>
      <div className="mb-4 flex items-center justify-between">
        <p className="font-display text-base font-semibold text-pine">{initial?.id ? "Edit" : "Add"} {spec.singular}</p>
        <button type="button" onClick={onCancel} className="text-ink/50 hover:text-ink" aria-label="Cancel" data-testid={`${name}-form-cancel`}>
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        {spec.fields.map((f) => (
          <div key={f.key} className={f.type === "textarea" || f.type === "image" ? "md:col-span-2" : ""}>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-ink/60">
              {f.label} {f.required && <span className="text-terra">*</span>}
            </label>
            <FieldInput f={f} value={form[f.key] ?? ""} onChange={(v) => setForm({ ...form, [f.key]: v })} testid={`${name}-field-${f.key}`} />
          </div>
        ))}
      </div>
      <div className="mt-5 flex gap-2">
        <button type="submit" disabled={saving} className="rounded-md bg-pine px-5 py-2 text-sm font-semibold text-white disabled:opacity-60" data-testid={`${name}-form-save`}>
          {saving ? "Saving…" : "Save"}
        </button>
        <button type="button" onClick={onCancel} className="rounded-md border border-line px-5 py-2 text-sm font-semibold text-ink/70">
          Cancel
        </button>
      </div>
    </form>
  );
}

export default function CollectionManager({ name }) {
  const spec = COLLECTION_SPECS[name];
  const [items, setItems] = useState([]);
  const [visible, setVisible] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | item
  const [loading, setLoading] = useState(true);

  const load = () =>
    axios.get(`${API}/${name}`).then((r) => {
      setItems(r.data.items);
      setVisible(r.data.visible);
      setLoading(false);
    });

  useEffect(() => {
    load();
  }, [name]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = async (v) => {
    setVisible(v);
    try {
      await axios.put(`${API}/admin/settings`, { [spec.settingKey]: v }, AUTH);
      toast.success(v ? `${spec.title} is now visible on the site` : `${spec.title} hidden from the site`);
    } catch {
      setVisible(!v);
      toast.error("Could not update visibility");
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete "${spec.summary(it)[0]}"?`)) return;
    await axios.delete(`${API}/admin/${name}/${it.id}`, AUTH);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="space-y-6" data-testid={`admin-${name}-panel`}>
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-[10px] border border-line bg-bone p-5">
        <div className="flex items-start gap-3">
          {visible ? <Eye className="mt-0.5 h-5 w-5 text-pine" /> : <EyeOff className="mt-0.5 h-5 w-5 text-ink/40" />}
          <div>
            <p className="font-display text-base font-semibold text-pine">{spec.title}</p>
            <p className="mt-0.5 max-w-xl text-xs text-ink/60">{spec.hint}</p>
          </div>
        </div>
        <label className="flex items-center gap-3 text-sm font-semibold text-ink">
          <span data-testid={`${name}-visibility-label`}>{visible ? "Visible on site" : "Hidden"}</span>
          <Switch checked={visible} onCheckedChange={toggle} data-testid={`${name}-visibility-toggle`} className="data-[state=checked]:bg-pine" />
        </label>
      </div>

      {editing && (
        <ItemForm
          name={name}
          spec={spec}
          initial={editing === "new" ? null : editing}
          onDone={() => {
            setEditing(null);
            load();
          }}
          onCancel={() => setEditing(null)}
        />
      )}

      <div className="flex items-center justify-between">
        <p className="text-sm text-ink/60" data-testid={`${name}-count`}>
          {loading ? "Loading…" : `${items.length} item${items.length === 1 ? "" : "s"}`}
        </p>
        {!editing && (
          <button onClick={() => setEditing("new")} className="inline-flex items-center gap-1.5 rounded-md bg-pine px-4 py-2 text-sm font-semibold text-white" data-testid={`${name}-add-button`}>
            <Plus className="h-4 w-4" /> Add
          </button>
        )}
      </div>

      <ul className="divide-y divide-line rounded-[10px] border border-line bg-surface" data-testid={`${name}-list`}>
        {items.map((it, i) => {
          const [a, b] = spec.summary(it);
          return (
            <li key={it.id} className="flex items-center gap-4 p-4" data-testid={`${name}-item-${i}`}>
              {it.photo ? (
                <img src={it.photo} alt="" className="h-12 w-12 shrink-0 rounded-md object-cover" />
              ) : (
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-bone font-display text-sm font-semibold text-pine">{i + 1}</span>
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate font-display text-sm font-semibold text-ink">{a}</p>
                <p className="truncate text-xs text-ink/55">{b}</p>
              </div>
              <button onClick={() => setEditing(it)} className="rounded-md border border-line p-2 text-ink/60 hover:border-pine hover:text-pine" aria-label="Edit" data-testid={`${name}-edit-${i}`}>
                <Pencil className="h-4 w-4" />
              </button>
              <button onClick={() => remove(it)} className="rounded-md border border-line p-2 text-ink/60 hover:border-terra hover:text-terra" aria-label="Delete" data-testid={`${name}-delete-${i}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </li>
          );
        })}
        {!loading && items.length === 0 && <li className="p-6 text-center text-sm text-ink/50">Nothing here yet. Add the first entry.</li>}
      </ul>
    </div>
  );
}
